import React from "react";
import { Link } from "react-router-dom";

type BaseButtonProps = {
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconClass?: string;
  iconWrapperClassName?: string;
  labelClassName?: string;
  iconPosition?: "left" | "right";
};

type AnchorButtonProps = BaseButtonProps & {
  href: string;
  to?: never;
  target?: string;
  rel?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type RouterLinkButtonProps = BaseButtonProps & {
  to: string;
  href?: never;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  replace?: boolean;
  state?: unknown;
};

type NativeButtonProps = BaseButtonProps & {
  href?: never;
  to?: never;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export type ButtonProps = AnchorButtonProps | RouterLinkButtonProps | NativeButtonProps;

const isRouterLinkProps = (props: ButtonProps): props is RouterLinkButtonProps =>
  typeof (props as RouterLinkButtonProps).to === "string";

const isAnchorProps = (props: ButtonProps): props is AnchorButtonProps =>
  typeof (props as AnchorButtonProps).href === "string";

const Button: React.FC<ButtonProps> = (props) => {
  const {
    className,
    style,
    ariaLabel,
    children,
    icon,
    iconClass,
    iconWrapperClassName,
    labelClassName,
    iconPosition = "left",
  } = props;

  const resolvedIcon = icon ?? (iconClass ? <i className={iconClass} /> : null);
  const iconNode = resolvedIcon ? (
    <span className={iconWrapperClassName} aria-hidden="true">
      {resolvedIcon}
    </span>
  ) : null;

  const labelNode = labelClassName ? (
    <span className={labelClassName}>{children}</span>
  ) : (
    children
  );

  const content =
    iconPosition === "right" ? (
      <>
        {labelNode}
        {iconNode}
      </>
    ) : (
      <>
        {iconNode}
        {labelNode}
      </>
    );

  if (isRouterLinkProps(props)) {
    const { to, onClick, replace, state } = props;
    return (
      <Link
        to={to}
        replace={replace}
        state={state}
        className={className}
        style={style}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  if (isAnchorProps(props)) {
    const { href, target, rel, onClick } = props;
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={className}
        style={style}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  const { type = "button", disabled, onClick } = props;
  return (
    <button
      type={type}
      disabled={disabled}
      className={className}
      style={style}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {content}
    </button>
  );
};

export default Button;
